import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  type DeepResearchDataRow,
  useCreateAIFieldDefinition,
  useCreateDeepResearchTasks,
  useDeleteDeepResearchReport,
  usePopulateRecordsList,
  useUpdateDeepResearchReport,
} from "@/hooks/api/use-deep-research";
import { useDRRAdminReview } from "@/hooks/use-feature-flags";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  buildFilterGroupFromColumnState,
  convertToClientFilters,
} from "@/lib/filters";
import { useDeepResearchReportsStore } from "@/store/deep-research-reports.store";
import type {
  AIFieldDefinition,
  DeepResearchReportMetadata,
} from "@/types/deep-research-reports";
import { DatasetReportLayout } from "./dataset-report-layout";
import { DeepResearchDetailsSheet } from "./deep-research-details-sheet";
import { DRRAccountsTable } from "./drr-accounts-table";
import { DRROpportunitiesTable } from "./drr-opportunities-table";
import { MAX_GENERATIONS_LIMIT } from "./drr-table-common";

interface DatasetReportProps {
  reportId: string;
  mode?: "editor" | "viewer"; // Optional - auto-determined from status if not provided
}

// Isolated sidebar component to prevent main table re-renders
interface DetailsSidebarProps {
  report: DeepResearchReportMetadata;
  effectiveMode: string;
}

const DetailsSidebar = memo(
  ({ report, effectiveMode }: DetailsSidebarProps) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<DeepResearchDataRow | null>(
      null,
    );

    // Reset selection when mode changes
    useEffect(() => {
      if (effectiveMode !== "viewer") {
        setSelectedRow(null);
        setIsDetailsOpen(false);
      }
    }, [effectiveMode]);

    // Expose a method to open the sidebar (called from parent)
    useEffect(() => {
      const handleRowSelect = (e: Event) => {
        const customEvent = e as CustomEvent<DeepResearchDataRow>;
        setSelectedRow(customEvent.detail);
        setIsDetailsOpen(true);
      };

      window.addEventListener("deep-research-row-select", handleRowSelect);
      return () =>
        window.removeEventListener("deep-research-row-select", handleRowSelect);
    }, []);

    if (effectiveMode !== "viewer") return null;

    return (
      <DeepResearchDetailsSheet
        report={report}
        row={selectedRow}
        isOpen={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
      />
    );
  },
);
DetailsSidebar.displayName = "DetailsSidebar";

export function DatasetReport({ reportId, mode }: DatasetReportProps) {
  // Auto-determine mode from status if not provided
  const apiReport = useDeepResearchReportsStore(
    (state) => state.byId[reportId],
  );
  if (!apiReport) {
    throw new Error(`Report ${reportId} not found`);
  }

  const aiFieldsCount = apiReport.ai_fields?.length ?? 0;

  const effectiveMode =
    mode ||
    (apiReport.status === "active" || apiReport.status === "complete"
      ? "viewer"
      : "editor");

  // Editor-specific state
  const [isEditingName, setIsEditingName] = useState(false);
  const [currentRecordCount, setCurrentRecordCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<Record<string, unknown>>(
    {},
  );

  // API hooks
  const updateReportMutation = useUpdateDeepResearchReport();
  const createAIFieldMutation = useCreateAIFieldDefinition();
  const populateRecordsMutation = usePopulateRecordsList();
  const createTasksMutation = useCreateDeepResearchTasks();
  const deleteReportMutation = useDeleteDeepResearchReport();
  const navigate = useNavigate();

  // Feature flags
  const isDRRAdmin = useDRRAdminReview();

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Handle filter changes from the DRR tables in draft mode
  const handleDraftFiltersChange = useCallback(
    (newFilters: Record<string, unknown>) => {
      setCurrentFilters(newFilters);
      if (apiReport.status !== "draft") return;

      const nextGroup = buildFilterGroupFromColumnState(newFilters);
      const currentGroup = ((): unknown => {
        const rf = apiReport.row_filters;
        if (rf && typeof rf === "object" && !Array.isArray(rf)) {
          if ("operator" in rf && "filters" in rf) return rf;
        }
        return undefined;
      })() as { operator: string; filters: unknown[] } | undefined;

      const hasCurrent = !!(
        currentGroup &&
        Array.isArray(currentGroup.filters) &&
        currentGroup.filters.length > 0
      );
      const hasNext = !!(
        nextGroup &&
        typeof nextGroup === "object" &&
        "filters" in nextGroup &&
        Array.isArray((nextGroup as { filters?: unknown[] }).filters) &&
        ((nextGroup as { filters?: unknown[] }).filters?.length ?? 0) > 0
      );

      // Skip PATCH when only sort/search changes (no structured filters on either side)
      if (!hasCurrent && !hasNext) return;

      // Only PATCH when the structured filters actually change
      const currStr = hasCurrent ? JSON.stringify(currentGroup) : "";
      const nextStr = hasNext ? JSON.stringify(nextGroup) : "";
      if (currStr === nextStr) return;

      updateReportMutation.mutate({
        id: apiReport.id,
        row_filters: nextGroup || {},
      });
    },
    [
      apiReport.id,
      apiReport.status,
      apiReport.row_filters,
      updateReportMutation,
    ],
  );

  usePageTitle(apiReport.name || "Deep Research Dataset");

  const isLocked = apiReport.status !== "draft";

  // Editor-specific handlers
  const handleNameSave = (newName: string) => {
    // Directly use the new name passed from the child component
    updateReportMutation.mutate(
      { id: apiReport.id, name: newName },
      {
        onSuccess: () => {
          setIsEditingName(false);
          toast.success("Dataset name updated");
        },
        onError: () => {
          toast.error("Failed to update dataset name");
        },
      },
    );
  };

  const handleSubmit = () => {
    if (apiReport.status === "draft") {
      updateReportMutation.mutate(
        {
          id: apiReport.id,
        },
        {
          onSuccess: () => {
            populateRecordsMutation.mutate(apiReport.id, {
              onSuccess: (data) => {
                const recordCount = data.record_count || 0;
                toast.success(
                  `Dataset submitted for review with ${recordCount} records`,
                );
              },
              onError: () => {
                toast.error("Failed to submit dataset for review");
              },
            });
          },
          onError: () => {
            toast.error("Failed to save dataset configuration");
          },
        },
      );
    }
  };

  const handleCreateTasks = () => {
    if (apiReport.status === "pending_review" && isDRRAdmin) {
      createTasksMutation.mutate(apiReport.id, {
        onSuccess: (data) => {
          const taskCount = data.task_count || 0;
          toast.success(`Created ${taskCount} AI generation tasks`);
        },
        onError: () => {
          toast.error("Failed to create AI generation tasks");
        },
      });
    }
  };

  const handleTypeChange = (value: "static" | "dynamic") => {
    if (!isLocked) {
      updateReportMutation.mutate({
        id: apiReport.id,
        dataset_type: value,
      });
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteReportMutation.mutate(apiReport.id, {
      onSuccess: () => {
        toast.success(`Dataset "${apiReport.name}" deleted successfully`);
        setDeleteDialogOpen(false);
        // Navigate back to the dataset list
        navigate("/deep-research");
      },
      onError: () => {
        toast.error("Failed to delete dataset");
      },
    });
  };

  // Hydrate editor filters from stored report filters on first load
  useEffect(() => {
    if (effectiveMode !== "editor") return;
    // Avoid overwriting user-edited filters
    if (Object.keys(currentFilters).length > 0) return;
    const rf = apiReport.row_filters;
    if (!rf || typeof rf !== "object" || Array.isArray(rf)) return;

    try {
      const hydrated =
        "operator" in rf && "filters" in rf
          ? convertToClientFilters(rf)
          : (rf as Record<string, unknown>);
      setCurrentFilters(hydrated || {});
    } catch {
      // Fallback to empty on any parsing issues
      setCurrentFilters({});
    }
  }, [effectiveMode, apiReport.row_filters, currentFilters]);

  // Handle AI field creation events from DRR tables
  useEffect(() => {
    const handler = async (e: Event) => {
      const evt = e as CustomEvent<{
        entityType: "opportunity" | "account";
        reportId: string;
        field: Partial<AIFieldDefinition>;
      }>;
      const detail = evt.detail;
      if (!detail) return;
      const { entityType, reportId: eid, field } = detail;
      if (!eid || !field) return;

      try {
        // Create AI field with the configuration from the modal
        const created = await createAIFieldMutation.mutateAsync({
          entity_type: entityType,
          display_name: field.display_name || "AI Field",
          description: field.description || undefined,
          content_type: field.content_type || "short_text",
          allowed_values: field.allowed_values || [],
          output_shape: field.output_shape || "string",
          ai_instructions: field.ai_instructions || "",
        });

        // Append to report's visible_ai_field_ids
        const currentIds = (apiReport.ai_fields || []).map((f) => f.id);
        const nextIds = Array.from(new Set([...currentIds, created.id]));
        updateReportMutation.mutate({
          id: apiReport.id,
          visible_ai_field_ids: nextIds,
        });
      } catch {
        // No-op; errors surfaced via hooks/toasts elsewhere
      }
    };

    window.addEventListener("drr-create-ai-field", handler as EventListener);
    return () =>
      window.removeEventListener(
        "drr-create-ai-field",
        handler as EventListener,
      );
  }, [apiReport, createAIFieldMutation, updateReportMutation]);

  const handlePause = () => {
    console.log("Pause dataset refresh");
    toast.info("Pausing dataset refresh");
  };

  const handleResume = () => {
    console.log("Resume dataset refresh");
    toast.info("Resuming dataset refresh");
  };

  const handleRowSelect = useCallback(
    (row: unknown) => {
      if (effectiveMode !== "viewer") return;
      const isDeepResearchRow = (
        candidate: unknown,
      ): candidate is DeepResearchDataRow => {
        return (
          typeof candidate === "object" &&
          candidate !== null &&
          "data" in candidate &&
          "entity_id" in candidate
        );
      };
      if (isDeepResearchRow(row)) {
        // Dispatch custom event instead of updating state
        window.dispatchEvent(
          new CustomEvent("deep-research-row-select", { detail: row }),
        );
      }
    },
    [effectiveMode],
  );

  return (
    <DatasetReportLayout
      mode={effectiveMode}
      report={apiReport}
      isEditingName={isEditingName}
      isLocked={isLocked}
      currentRecordCount={currentRecordCount}
      datasetType={apiReport.dataset_type}
      entityType={apiReport.entity_type ?? "opportunity"}
      aiColumnsCount={aiFieldsCount}
      onEditName={() => !isLocked && setIsEditingName(true)}
      onSaveEditName={handleNameSave}
      onCancelEditName={() => {
        setIsEditingName(false);
      }}
      onSubmit={handleSubmit}
      onCreateTasks={handleCreateTasks}
      showCreateTasksButton={
        isDRRAdmin && apiReport.status === "pending_review"
      }
      isCreatingTasks={createTasksMutation.isPending}
      onDelete={apiReport.status === "draft" ? handleDelete : undefined}
      onTypeChange={handleTypeChange}
      onEntityTypeChange={(value) => {
        if (!isLocked) {
          // Clear all entity-specific data when changing entity type
          updateReportMutation.mutate(
            {
              id: apiReport.id,
              entity_type: value,
              row_filters: {}, // Clear saved filters (entity-specific)
              visible_ai_field_ids: [], // Clear AI field selections (entity-specific)
            },
            {
              onSuccess: () => {
                toast.info(
                  "Entity type changed. Filters and AI fields have been cleared.",
                );
              },
              onError: () => {
                toast.error("Failed to change entity type");
              },
            },
          );
        }
      }}
      onPause={handlePause}
      onResume={handleResume}
      isUpdatingName={updateReportMutation.isPending}
      isSubmitting={
        updateReportMutation.isPending || populateRecordsMutation.isPending
      }
      isDeleting={deleteReportMutation.isPending}
      showValidationAlerts={effectiveMode === "editor"}
      submitDisabled={
        aiFieldsCount === 0 ||
        currentRecordCount === 0 ||
        currentRecordCount * aiFieldsCount > MAX_GENERATIONS_LIMIT ||
        updateReportMutation.isPending ||
        populateRecordsMutation.isPending
      }
    >
      {/* Use the appropriate entity-specific table based on entity type */}
      {apiReport.entity_type === "opportunity" && (
        <DRROpportunitiesTable
          report={apiReport}
          mode={effectiveMode}
          onRowSelect={handleRowSelect}
          filters={effectiveMode === "editor" ? currentFilters : undefined}
          onFiltersChange={
            effectiveMode === "editor" ? handleDraftFiltersChange : undefined
          }
          onRecordCountChange={setCurrentRecordCount}
        />
      )}

      {apiReport.entity_type === "account" && (
        <DRRAccountsTable
          report={apiReport}
          mode={effectiveMode}
          onRowSelect={handleRowSelect}
          filters={effectiveMode === "editor" ? currentFilters : undefined}
          onFiltersChange={
            effectiveMode === "editor" ? handleDraftFiltersChange : undefined
          }
          onRecordCountChange={setCurrentRecordCount}
        />
      )}

      {!apiReport.entity_type && (
        <div className="p-8 text-center text-muted-foreground">
          Please select an entity type to begin.
        </div>
      )}

      <DetailsSidebar report={apiReport} effectiveMode={effectiveMode} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dataset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{apiReport.name}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteReportMutation.isPending}
            >
              {deleteReportMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DatasetReportLayout>
  );
}
